import React, { useState, useEffect, useCallback } from 'react';
import { Item, useCanvasStore } from '@/stores/canvasStore';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Bold, 
  Italic, 
  Underline,
  List, 
  ListOrdered,
  Heading1,
  Heading2,
  Code,
  Quote,
  Download,
  Copy,
  Sparkles,
  Undo,
  Redo,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import UnderlineExtension from '@tiptap/extension-underline';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import LinkExtension from '@tiptap/extension-link';
import { InlineAIPanel } from '@/components/ai';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface DocumentEditorProps {
  item: Item;
  isOpen: boolean;
  onClose: () => void;
}

export function DocumentEditor({ item, isOpen, onClose }: DocumentEditorProps) {
  const content = item.content as { title?: string; body?: string; wordCount?: number };
  const [title, setTitle] = useState(content.title || '');
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [aiPanelPosition, setAIPanelPosition] = useState({ top: 0, left: 0 });
  const { updateItem, setIsEditingItem } = useCanvasStore();

  // Initialize TipTap editor
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
        },
      }),
      UnderlineExtension,
      TaskList,
      TaskItem.configure({
        nested: true,
      }),
      LinkExtension.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary underline cursor-pointer',
        },
      }),
      Placeholder.configure({
        placeholder: 'Start writing your document...',
      }),
    ],
    content: content.body || '',
    editorProps: {
      attributes: {
        class: 'prose prose-sm dark:prose-invert max-w-none focus:outline-none min-h-full prose-ul:list-disc prose-ul:pl-5 prose-ol:list-decimal prose-ol:pl-5 prose-li:my-1 [&_ul[data-type="taskList"]]:list-none [&_ul[data-type="taskList"]]:pl-0',
      },
      handleKeyDown: (view, event) => {
        // Handle Tab for list indentation
        if (event.key === 'Tab') {
          const { state } = view;
          const { selection } = state;
          
          // Check if we're in a list
          const { $from } = selection;
          const listItem = $from.node(-1);
          
          if (listItem?.type.name === 'listItem') {
            event.preventDefault();
            if (event.shiftKey) {
              // Outdent: lift list item
              return editor?.chain().liftListItem('listItem').run() ?? false;
            } else {
              // Indent: sink list item
              return editor?.chain().sinkListItem('listItem').run() ?? false;
            }
          }
        }
        return false;
      },
    },
    onSelectionUpdate: ({ editor }) => {
      const { from, to } = editor.state.selection;
      if (from !== to) {
        const text = editor.state.doc.textBetween(from, to, ' ');
        setSelectedText(text);
      } else {
        setSelectedText('');
      }
    },
  });

  // Update editor content when item changes
  useEffect(() => {
    if (editor && isOpen) {
      const newBody = (item.content as { body?: string }).body || '';
      if (editor.getHTML() !== newBody) {
        editor.commands.setContent(newBody);
      }
      setTitle((item.content as { title?: string }).title || '');
    }
  }, [item.content, isOpen, editor]);

  // Notify store when editor opens/closes
  useEffect(() => {
    setIsEditingItem(isOpen);
    return () => setIsEditingItem(false);
  }, [isOpen, setIsEditingItem]);

  const getWordCount = useCallback(() => {
    if (!editor) return 0;
    const text = editor.state.doc.textContent;
    return text.trim() ? text.trim().split(/\s+/).length : 0;
  }, [editor]);

  const handleSave = useCallback(() => {
    if (!editor) return;
    const body = editor.getHTML();
    const wordCount = getWordCount();
    updateItem(item.id, {
      content: { title, body, wordCount },
    });
    onClose();
  }, [editor, updateItem, item.id, title, getWordCount, onClose]);

  const handleCopyAsText = () => {
    if (!editor) return;
    const text = editor.state.doc.textContent;
    navigator.clipboard.writeText(`${title}\n\n${text}`);
    toast.success('Copied to clipboard');
  };

  const handleDownloadAsText = () => {
    if (!editor) return;
    const text = editor.state.doc.textContent;
    const content = `${title || 'Untitled Document'}\n\n${text}`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title || 'document'}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Downloaded');
  };

  // Open AI panel
  const handleOpenAI = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      setAIPanelPosition({
        top: rect.bottom + 10,
        left: Math.max(rect.left, 100),
      });
    }
    setShowAIPanel(true);
  };

  // Replace selected text with AI result
  const handleAIReplace = (newText: string) => {
    if (!editor) return;
    editor.chain().focus().deleteSelection().insertContent(newText).run();
    setShowAIPanel(false);
    setSelectedText('');
    toast.success('Text replaced');
  };

  // Insert AI result below selection
  const handleAIInsert = (newText: string) => {
    if (!editor) return;
    const { to } = editor.state.selection;
    editor.chain().focus().setTextSelection(to).insertContent(`\n\n${newText}`).run();
    setShowAIPanel(false);
    setSelectedText('');
    toast.success('Text inserted');
  };

  if (!editor) {
    return null;
  }

  const toolbarButtons = [
    { 
      icon: Bold, 
      action: () => editor.chain().focus().toggleBold().run(),
      isActive: editor.isActive('bold'),
      title: 'Bold',
      shortcut: '⌘B'
    },
    { 
      icon: Italic, 
      action: () => editor.chain().focus().toggleItalic().run(),
      isActive: editor.isActive('italic'),
      title: 'Italic',
      shortcut: '⌘I'
    },
    { 
      icon: Underline, 
      action: () => editor.chain().focus().toggleUnderline().run(),
      isActive: editor.isActive('underline'),
      title: 'Underline',
      shortcut: '⌘U'
    },
    { type: 'divider' as const },
    { 
      icon: Heading1, 
      action: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
      isActive: editor.isActive('heading', { level: 1 }),
      title: 'Heading 1' 
    },
    { 
      icon: Heading2, 
      action: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
      isActive: editor.isActive('heading', { level: 2 }),
      title: 'Heading 2' 
    },
    { type: 'divider' as const },
    { 
      icon: List, 
      action: () => editor.chain().focus().toggleBulletList().run(),
      isActive: editor.isActive('bulletList'),
      title: 'Bullet List' 
    },
    { 
      icon: ListOrdered, 
      action: () => editor.chain().focus().toggleOrderedList().run(),
      isActive: editor.isActive('orderedList'),
      title: 'Numbered List' 
    },
    { type: 'divider' as const },
    { 
      icon: Quote, 
      action: () => editor.chain().focus().toggleBlockquote().run(),
      isActive: editor.isActive('blockquote'),
      title: 'Quote' 
    },
    { 
      icon: Code, 
      action: () => editor.chain().focus().toggleCodeBlock().run(),
      isActive: editor.isActive('codeBlock'),
      title: 'Code Block' 
    },
    { type: 'divider' as const },
    { 
      icon: Undo, 
      action: () => editor.chain().focus().undo().run(),
      isActive: false,
      title: 'Undo',
      shortcut: '⌘Z',
      disabled: !editor.can().undo(),
    },
    { 
      icon: Redo, 
      action: () => editor.chain().focus().redo().run(),
      isActive: false,
      title: 'Redo',
      shortcut: '⌘⇧Z',
      disabled: !editor.can().redo(),
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleSave()}>
      <DialogContent 
        className="max-w-5xl h-[85vh] flex flex-col p-0 gap-0 rounded-3xl border-0 shadow-2xl overflow-hidden bg-background [&>button:last-child]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
         onWheelCapture={(e) => {
           // Hard-stop wheel events from ever reaching the canvas/page behind the modal.
           // (Also prevents trackpad back/forward swipe gestures from triggering when editing.)
           e.stopPropagation();
           if (Math.abs(e.deltaX) > Math.abs(e.deltaY) * 0.5) {
             e.preventDefault();
           }
         }}
         onTouchMoveCapture={(e) => {
           // Prevent touch scroll chaining to the canvas behind the modal
           e.stopPropagation();
         }}
      >
        {/* Header - Clean and minimal */}
        <DialogHeader className="flex flex-row items-center justify-between px-6 py-4 border-b border-border/50 bg-muted/30">
          <DialogTitle className="flex-1 sr-only">Document Editor</DialogTitle>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Untitled Document"
            className="text-lg font-semibold border-none bg-transparent px-2 h-auto py-1 flex-1 min-w-0 focus-visible:ring-0 placeholder:text-muted-foreground/50"
          />
          <div className="flex items-center gap-1 ml-4">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={handleCopyAsText}
                  className="h-9 w-9 rounded-xl text-muted-foreground hover:text-foreground"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Copy as text</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={handleDownloadAsText}
                  className="h-9 w-9 rounded-xl text-muted-foreground hover:text-foreground"
                >
                  <Download className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Download as .txt</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <DialogClose asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-9 w-9 rounded-xl text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </DialogClose>
              </TooltipTrigger>
              <TooltipContent>Close</TooltipContent>
            </Tooltip>
          </div>
        </DialogHeader>
        
        {/* Main content area with vertical toolbar on LEFT and editor */}
        <div className="flex-1 flex overflow-hidden">
          {/* Vertical Toolbar - Floating pill style */}
          <div className="w-14 bg-muted/30 border-r border-border/50 flex flex-col items-center py-4 gap-1">
            {toolbarButtons.map((btn, index) => {
              if ('type' in btn && btn.type === 'divider') {
                return <div key={index} className="h-px w-8 bg-border/50 my-2" />;
              }
              const { icon: Icon, action, isActive, title, shortcut, disabled } = btn as {
                icon: React.ComponentType<{ className?: string }>;
                action: () => void;
                isActive: boolean;
                title: string;
                shortcut?: string;
                disabled?: boolean;
              };
              return (
                <Tooltip key={title}>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={action}
                      disabled={disabled}
                      className={cn(
                        "h-9 w-9 rounded-xl shrink-0 transition-all duration-150",
                        isActive 
                          ? "bg-primary text-primary-foreground shadow-sm" 
                          : "text-muted-foreground hover:text-foreground hover:bg-muted",
                        disabled && "opacity-40"
                      )}
                    >
                      <Icon className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    {title}{shortcut && <span className="ml-2 text-muted-foreground">{shortcut}</span>}
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
          
          {/* Editor Area */}
          <div 
            className="flex-1 overflow-auto scroll-smooth p-8 bg-background cursor-text overscroll-contain"
            onClick={() => editor.chain().focus().run()}
            onWheel={(e) => {
              const target = e.currentTarget;
               // Prevent trackpad horizontal swipe (back/forward) while inside the editor
               if (Math.abs(e.deltaX) > Math.abs(e.deltaY) * 0.5) {
                 e.preventDefault();
                 e.stopPropagation();
                 return;
               }

              const { scrollTop, scrollHeight, clientHeight } = target;
               const atTop = scrollTop <= 0;
               const atBottom = scrollTop + clientHeight >= scrollHeight - 1;
              
              // Prevent scroll from propagating when at boundaries
              if ((atTop && e.deltaY < 0) || (atBottom && e.deltaY > 0)) {
                 e.preventDefault();
                e.stopPropagation();
              }
            }}
          >
            <div className="max-w-3xl mx-auto">
              <EditorContent 
                editor={editor} 
                className="min-h-full"
              />
            </div>
          </div>
        </div>
        
        {/* Footer - Polished */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border/50 bg-muted/30">
          <span className="text-sm text-muted-foreground font-medium">
            {getWordCount()} words
            {selectedText.length > 0 && (
              <span className="ml-2 text-primary">• {selectedText.length} selected</span>
            )}
          </span>
          <div className="flex gap-3">
            <Button 
              variant="ghost" 
              onClick={onClose}
              className="rounded-xl px-6"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              className="rounded-xl px-6 shadow-sm"
            >
              Save
            </Button>
          </div>
        </div>

        {/* Floating AI Button - Bottom right */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={handleOpenAI}
              className={cn(
                "absolute bottom-24 right-6 w-11 h-11 rounded-2xl flex items-center justify-center transition-all duration-200 z-10 shadow-lg",
                showAIPanel
                  ? "bg-partnr text-partnr-foreground scale-110"
                  : "bg-background hover:bg-partnr/10 text-partnr border border-border/50 hover:border-partnr/50"
              )}
            >
              <Sparkles className="w-5 h-5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="left">AI Assistant</TooltipContent>
        </Tooltip>

        {/* AI Panel */}
        {showAIPanel && (
          <InlineAIPanel
            selectedText={selectedText}
            fullContent={editor?.getText() || ''}
            onReplace={handleAIReplace}
            onInsert={handleAIInsert}
            onClose={() => setShowAIPanel(false)}
            position={aiPanelPosition}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
